/**
 * Subscriptions list — superadmin red panel (Phase 3).
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { superadminAPI } from '../../services/api'
import {
  HiOutlineUserGroup,
  HiOutlineMagnifyingGlass,
  HiOutlineArrowPath,
  HiOutlineEye,
} from 'react-icons/hi2'

const STATUS_STYLES = {
  trial:         'bg-info-soft text-info',
  active:        'bg-green-100 text-green-800',
  past_due:      'bg-amber-100 text-amber-800',
  blocked:       'bg-red-100 text-red-800',
  cancelled:     'bg-gray-100 text-gray-700',
  expired:       'bg-gray-100 text-gray-700',
  grandfathered: 'bg-indigo-100 text-indigo-800',
  pending:       'bg-gray-100 text-gray-700',
}

export default function SubscriptionsPage() {
  const navigate = useNavigate()
  const [data, setData] = useState({ items: [], total: 0 })
  const [loading, setLoading] = useState(true)
  const [params, setParams] = useState({ status: '', q: '', limit: 50, offset: 0 })

  const reload = useCallback(async () => {
    setLoading(true)
    try {
      const res = await superadminAPI.listSubscriptions(params)
      const payload = res?.data?.data ?? res?.data ?? { items: [], total: 0 }
      setData(payload)
    } catch (e) {
      setData({ items: [], total: 0 })
    } finally {
      setLoading(false)
    }
  }, [params])

  useEffect(() => { reload() }, [reload])

  const statuses = useMemo(() => [
    '', 'trial', 'active', 'past_due', 'blocked', 'cancelled', 'expired', 'grandfathered', 'pending',
  ], [])

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <HiOutlineUserGroup className="w-6 h-6 text-danger" /> Subscriptions
          </h1>
          <p className="text-gray-600 mt-1 text-sm">All workspace subscriptions across the system.</p>
        </div>
        <button
          onClick={reload}
          className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-2"
        >
          <HiOutlineArrowPath className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm p-4 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-600">Status:</label>
          <select
            value={params.status}
            onChange={(e) => setParams(p => ({ ...p, status: e.target.value, offset: 0 }))}
            className="border border-gray-300 rounded px-2 py-1.5 text-sm"
          >
            {statuses.map(s => <option key={s} value={s}>{s || 'All'}</option>)}
          </select>
        </div>
        <div className="flex items-center gap-2 flex-1 min-w-[200px]">
          <HiOutlineMagnifyingGlass className="w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search workspace name"
            value={params.q}
            onChange={(e) => setParams(p => ({ ...p, q: e.target.value, offset: 0 }))}
            className="flex-1 border border-gray-300 rounded px-3 py-1.5 text-sm"
          />
        </div>
        <span className="text-sm text-gray-500">{data.total} total</span>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50">
            <tr className="text-left text-gray-700 text-xs uppercase tracking-wide">
              <th className="px-4 py-3">Workspace</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Plan</th>
              <th className="px-4 py-3">Price</th>
              <th className="px-4 py-3">Renews / Ends</th>
              <th className="px-4 py-3">Stripe</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {data.items.map(sub => (
              <tr key={sub.id} className="hover:bg-gray-50">
                <td className="px-4 py-3 font-medium text-gray-900">{sub.account.name}</td>
                <td className="px-4 py-3">
                  <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${STATUS_STYLES[sub.status] || 'bg-gray-100 text-gray-700'}`}>
                    {sub.status}
                  </span>
                </td>
                <td className="px-4 py-3 text-gray-700">{sub.plan?.name || '—'}</td>
                <td className="px-4 py-3 text-gray-700">${sub.current_price_usd}</td>
                <td className="px-4 py-3 text-gray-600 text-xs">
                  {sub.trial_ends_at && <>Trial: {new Date(sub.trial_ends_at).toLocaleDateString()}</>}
                  {!sub.trial_ends_at && sub.current_period_end && new Date(sub.current_period_end).toLocaleDateString()}
                  {!sub.trial_ends_at && !sub.current_period_end && '—'}
                </td>
                <td className="px-4 py-3 text-gray-600 text-xs">
                  {sub.stripe_subscription_id ? (
                    <code className="bg-gray-100 px-1 rounded">{sub.stripe_subscription_id.slice(0, 16)}…</code>
                  ) : '—'}
                </td>
                <td className="px-4 py-3 text-right">
                  <button
                    onClick={() => navigate(`/superadmin/subscriptions/${sub.id}`)}
                    className="text-danger hover:text-red-800 inline-flex items-center gap-1 text-sm"
                  >
                    <HiOutlineEye className="w-4 h-4" /> View
                  </button>
                </td>
              </tr>
            ))}
            {data.items.length === 0 && !loading && (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-gray-500 text-sm">
                  No subscriptions match your filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
